import { useMemo, useRef, useEffect } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import {
  format,
  parse,
  startOfWeek,
  getDay,
  startOfDay,
  isSameDay,
} from "date-fns";
import es from "date-fns/locale/es";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./CalendarStyles.css";
import ModalBasicLayout from "../ModalBasicLayout";
import EventDetailsPopover from "./EventDetailsPopover";
import CalendarToolbar from "./CalendarToolbar";
import { CalendarEventComponent } from "./CalendarEventConfig";
import CalendarTimeHeader from "./CalendarTimeHeader";
import Box from "@mui/material/Box";
import Loader from "../../Loaders/Loader";
import { useTranslationApp } from "../../../lib/i18next";
import { useModalCalendar } from "../../../hooks/useModalCalendar";
import { useShareModalFlow } from "../../../hooks/useShareModalFlow";
import {
  BORDER_RADIUS,
  TIKTOK,
  YOUTUBE,
  INSTAGRAM,
  FACEBOOK,
  LINKEDIN,
} from "../../../utils/constants";
import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  CALENDAR_MONTH,
  CALENDAR_WEEK,
  CALENDAR_DAY,
  CALENDAR_TODAY,
  CALENDAR_NEXT,
  CALENDAR_PREVIOUS,
  CALENDAR_AGENDA,
  CALENDAR_INVALID_DATE_ERROR,
  WARNING,
} from "../../../i18n/keysTranslations";
import { SimpleAlert } from "../../Alerts/Alerts";
import { ALERT_ICON_TYPE_WARNING } from "../../../utils/constants";
import ShareModalMultiple from "../../Gallery/ShareModalMultiple/ShareModalMultiple";

const locales = {
  es,
  "en-US": enUS,
  en: enUS,
};

const platformMapping = {
  [TIKTOK]: TIKTOK,
  [YOUTUBE]: YOUTUBE,
  [INSTAGRAM]: INSTAGRAM,
  [FACEBOOK]: FACEBOOK,
  [LINKEDIN]: LINKEDIN,
};

const ModalCalendar = ({
  modalOpen,
  onCloseModal,
  navigateToScheduledRequest = null,
  onNavigateToScheduledConsumed,
  onRegisterRefreshScheduledContents,
}) => {
  const calendarRef = useRef(null);

  const { i18n, t } = useTranslationApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    isShareModalOpen,
    currentItem,
    scheduledDate,
    isEditing,
    editingData,
    openShareModalFromCalendar,
    openShareModalForEdit,
    closeShareModal,
  } = useShareModalFlow();

  const {
    anchorEl,
    selectedEvent,
    events,
    isLoading,
    view,
    date,
    culture,
    open,
    anchorRef,
    onEventClick,
    onClosePopover,
    onShowMore,
    onViewChange,
    onNavigate,
    eventPropGetter,
    refreshScheduledContents,
  } = useModalCalendar(modalOpen, {
    navigateToScheduledRequest,
    onNavigateToScheduledConsumed,
  });

  useEffect(() => {
    onRegisterRefreshScheduledContents?.(refreshScheduledContents);
    return () => {
      onRegisterRefreshScheduledContents?.(null);
    };
  }, [refreshScheduledContents, onRegisterRefreshScheduledContents]);

  const localizer = useMemo(() => {
    return dateFnsLocalizer({
      format,
      parse,
      startOfWeek:
        view === "week"
          ? (date) => startOfDay(date)
          : (date) => startOfWeek(date, { weekStartsOn: 1 }),
      getDay,
      locales,
    });
  }, [view]);

  const formats = useMemo(
    () => ({
      timeGutterFormat: (date, _culture, loc) =>
        loc.format(date, "hh:mm a", culture),
      eventTimeRangeFormat: ({ start }, _culture, loc) =>
        loc.format(start, "hh:mm a", culture),
    }),
    [culture]
  );

  const calendarMessages = useMemo(
    () => ({
      month: t(CALENDAR_MONTH),
      week: t(CALENDAR_WEEK),
      day: t(CALENDAR_DAY),
      today: t(CALENDAR_TODAY),
      next: t(CALENDAR_NEXT),
      previous: t(CALENDAR_PREVIOUS),
      agenda: t(CALENDAR_AGENDA),
      showMore: (total) =>
        `+ ${total} ${i18n.language?.startsWith("es") ? "más" : "more"}`,
    }),
    [t, i18n.language]
  );

  const calendarComponents = useMemo(
    () => {
      const MonthDateCell = ({ date, label }) => {
        const today = new Date();
        const todayStart = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );

        const cellDay = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        );

        const isPastDay = cellDay < todayStart;
        const isToday = isSameDay(cellDay, todayStart);

        const onDateClick = (e) => {
          if (isPastDay) {
            e.preventDefault();
            e.stopPropagation();
            SimpleAlert({
              title: t(WARNING),
              text: t(CALENDAR_INVALID_DATE_ERROR),
              icon: ALERT_ICON_TYPE_WARNING,
            });
            return false;
          }

          if (isToday) {
            e.preventDefault();
            e.stopPropagation();
            const now = new Date();
            const dateWith10Minutes = new Date(now.getTime() + 10 * 60 * 1000);
            openShareModalFromCalendar(dateWith10Minutes);
            return false;
          }
        };

        return (
          <div
            className={`rbc-date-cell ${isPastDay ? "rbc-disabled-date" : ""} ${isToday ? "rbc-today" : ""}`}
            onClick={onDateClick}
            onMouseDown={(e) => {
              if (isPastDay) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <button
              className="rbc-button-link"
              onClick={(e) => {
                if (isPastDay) {
                  e.preventDefault();
                  e.stopPropagation();
                  SimpleAlert({
                    title: t(WARNING),
                    text: t(CALENDAR_INVALID_DATE_ERROR),
                    icon: ALERT_ICON_TYPE_WARNING,
                  });
                  return false;
                }

                if (isToday) {
                  e.preventDefault();
                  e.stopPropagation();
                  const now = new Date();
                  const dateWith10Minutes = new Date(
                    now.getTime() + 10 * 60 * 1000
                  );
                  openShareModalFromCalendar(dateWith10Minutes);
                  return false;
                }
              }}
              disabled={isPastDay}
            >
              {label}
            </button>
          </div>
        );
      };

      return {
        event: CalendarEventComponent,
        toolbar: (props) => (
          <CalendarToolbar {...props} onCloseModal={onCloseModal} />
        ),
        month: {
          dateHeader: ({ date, label }) => (
            <MonthDateCell date={date} label={label} />
          ),
        },
        week: {
          header: ({ date, localizer }) => (
            <CalendarTimeHeader
              date={date}
              localizer={localizer}
              culture={culture}
            />
          ),
        },
        day: {
          header: ({ date, localizer }) => (
            <CalendarTimeHeader
              date={date}
              localizer={localizer}
              culture={culture}
            />
          ),
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [culture, t, openShareModalFromCalendar]
  );

  const onSelectSlot = ({ start, action }) => {
    console.log("onSelectSlot", start, action);
    if (!["click", "select"].includes(action)) return;

    const dateObj = new Date(start);
    const now = new Date();

    const dateObjStart = new Date(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate()
    );
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    if (dateObjStart < todayStart) {
      SimpleAlert({
        title: t(WARNING),
        text: t(CALENDAR_INVALID_DATE_ERROR),
        icon: ALERT_ICON_TYPE_WARNING,
      });
      return;
    }

    if (view === "month" && isSameDay(dateObjStart, todayStart)) {
      const dateWith10Minutes = new Date(now.getTime() + 10 * 60 * 1000);
      openShareModalFromCalendar(dateWith10Minutes);
      return;
    }

    if (isSameDay(dateObjStart, todayStart) && dateObj <= now) {
      SimpleAlert({
        title: t(WARNING),
        text: t(CALENDAR_INVALID_DATE_ERROR),
        icon: ALERT_ICON_TYPE_WARNING,
      });
      return;
    }

    openShareModalFromCalendar(dateObj);
  };

  const onEditEvent = (event) => {
    try {
      const originalData = event.originalData || {};
      const platformsData = originalData.platformsData || {};

      const isCarousel = Object.values(platformsData).some(
        (platformData) => platformData?.mediaType === "CAROUSEL"
      );

      let firstUrl = null;
      let urls = [];

      if (originalData.urls && Array.isArray(originalData.urls)) {
        urls = originalData.urls.map((url, index) => {
          const urlString = typeof url === "object" ? url.url || url : url;
          return {
            url: urlString,
            mediaData:
              originalData.mediaData?.[index] ||
              (typeof url === "object" ? url.mediaData : {}),
          };
        });
        firstUrl = urls[0]?.url || null;
      } else if (originalData.url) {
        firstUrl = originalData.url;
        urls = [
          {
            url: firstUrl,
            mediaData: originalData.mediaData?.[0] || {},
          },
        ];
      } else if (originalData.urls && typeof originalData.urls === "string") {
        firstUrl = originalData.urls;
        urls = [
          {
            url: firstUrl,
            mediaData: originalData.mediaData?.[0] || {},
          },
        ];
      }

      let platform = platformMapping[event.platform];

      const item =
        isCarousel && urls.length > 1
          ? {
              isCarousel: true,
              carouselImages: urls,
              url: firstUrl,
              mediaData: urls[0]?.mediaData || {},
            }
          : {
              url: firstUrl,
              mediaData:
                urls[0]?.mediaData || originalData.mediaData?.[0] || {},
            };

      const editingPayload = {
        id: originalData.cloudTaskID || originalData.id,
        queue: originalData.queue || "",
        scheduleID: event.id,
        platform,
        originalData,
        item,
      };

      const eventScheduledDate = new Date(event.start);
      openShareModalForEdit(editingPayload, eventScheduledDate);
      onClosePopover();
    } catch (error) {
      console.error("Error al abrir modal de edición:", error);
    }
  };

  const dayPropGetter = (date) => {
    const today = new Date();

    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const cellDay = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    if (cellDay < todayStart) {
      return { className: "rbc-disabled-day" };
    }

    return {};
  };

  const slotPropGetter = (date) => {
    const now = new Date();
    const isPast = date < now;

    return {
      className: isPast ? "rbc-disabled-slot slot-past" : "slot-future",
    };
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      maxWidth="lg"
      fullScreen={isMobile}
      hasTitle={!isMobile}
      aria-label="Calendario de agenda"
      sxTitle={
        isMobile
          ? {
              display: "none",
            }
          : {}
      }
      sxContent={{
        py: isMobile ? 1.5 : undefined,
        px: isMobile ? 0 : undefined,
        pb: isMobile ? 0 : undefined,
        overflow: "visible",
        height: isMobile ? "100%" : undefined,
      }}
      sx={{
        "& .MuiDialog-paper": isMobile
          ? {
              maxHeight: "100%",
              height: "100%",
              borderRadius: 0,
            }
          : {
              borderRadius: BORDER_RADIUS,
            },
      }}
    >
      <Box
        ref={calendarRef}
        sx={{
          "--calendar-text-primary": theme.palette.text.primary,
          "--calendar-text-secondary": theme.palette.text.secondary,
          "--calendar-bg-paper": theme.palette.background.paper,
          height: isMobile ? "100%" : "85vh",
          paddingBottom: isMobile ? 0 : "1rem",
          position: "relative",
          overflow: "visible",
        }}
      >
        <div
          ref={anchorRef}
          style={{ position: "absolute", pointerEvents: "none" }}
        />

        {isLoading ? (
          <Loader fullScreen={false} size={40} hasMarginTop={true} />
        ) : (
          <Calendar
            culture={culture}
            longPressThreshold={0.000001}
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            view={view}
            date={date}
            onView={onViewChange}
            onNavigate={onNavigate}
            views={["month", "week", "day"]}
            eventPropGetter={eventPropGetter}
            popup
            onShowMore={onShowMore}
            style={{ height: "100%" }}
            formats={formats}
            components={calendarComponents}
            onSelectEvent={onEventClick}
            messages={calendarMessages}
            selectable
            onSelectSlot={onSelectSlot}
            dayPropGetter={dayPropGetter}
            slotPropGetter={slotPropGetter}
          />
        )}
      </Box>

      <EventDetailsPopover
        open={open}
        anchorEl={anchorEl}
        selectedEvent={selectedEvent}
        onClose={onClosePopover}
        onEdit={onEditEvent}
        onRefreshCalendar={refreshScheduledContents}
      />

      {isShareModalOpen && (
        <ShareModalMultiple
          modalOpen={isShareModalOpen}
          onCloseModal={closeShareModal}
          item={currentItem}
          isEditing={isEditing}
          editingData={editingData}
          scheduledDate={scheduledDate}
          onRefreshCalendar={refreshScheduledContents}
        />
      )}
    </ModalBasicLayout>
  );
};

export default ModalCalendar;
